
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Order, OptionValue, ProductAddon, CartItem } from '../types';

interface MyPageProps {
  currentUser: User | null;
  orders: Order[];
  cancelledOrders: Order[];
  onUpdateUserInfo: (updatedUser: User) => void;
  onDeleteAccount: (userId: string) => void;
  onCancelOrder: (orderId: string) => void;
}

const MyPage: React.FC<MyPageProps> = ({ currentUser, orders, cancelledOrders, onUpdateUserInfo, onDeleteAccount, onCancelOrder }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') || 'orders';
  
  const [visibleOrdersCount, setVisibleOrdersCount] = useState(5);

  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    phone: currentUser?.phone || '',
    address: currentUser?.address || '',
    detailAddress: currentUser?.detailAddress || ''
  });

  useEffect(() => {
    setVisibleOrdersCount(5);
  }, [activeTab]);

  const allOrders = [...orders, ...cancelledOrders]
    .filter(o => o.userId === currentUser?.id)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const displayedOrders = allOrders.slice(0, visibleOrdersCount);

  const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 8) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 7)}-${phoneNumber.slice(7, 11)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData({ ...formData, [name]: formatPhoneNumber(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: (data: any) => {
        setFormData(prev => ({ ...prev, address: data.address }));
        document.getElementById('editDetailAddress')?.focus();
      }
    }).open();
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    onUpdateUserInfo({ ...currentUser, ...formData });
    if (window.customAlert) await window.customAlert('회원 정보가 수정되었습니다.');
    else alert('회원 정보가 수정되었습니다.');
  };

  const handleWithdraw = async () => {
    if (!currentUser) return;
    const confirmed = window.customConfirm 
      ? await window.customConfirm('정말 메이커스토리를 탈퇴하시겠습니까?\n탈퇴 시 모든 정보와 보유 포인트가 삭제되며 복구할 수 없습니다.')
      : window.confirm('정말 메이커스토리를 탈퇴하시겠습니까?\n탈퇴 시 모든 정보와 보유 포인트가 삭제되며 복구할 수 없습니다.');
      
    if (confirmed) {
      // [수정] 비동기 탈퇴 및 로그아웃 완료를 기다린 후 안내 메시지 출력
      await onDeleteAccount(currentUser.id);
      if (window.customAlert) await window.customAlert('회원 탈퇴가 성공적으로 완료되었습니다.\n함께해주신 시간 잊지 않겠습니다. 그동안 감사했습니다!');
      else alert('회원 탈퇴가 완료되었습니다. 그동안 이용해 주셔서 감사합니다.');
    }
  };

  const handleCancelClick = async (orderId: string) => {
    const confirmed = window.customConfirm
      ? await window.customConfirm('주문을 취소하시겠습니까?\n결제 금액이 실제 환불 처리되며, 사용한 포인트도 즉시 반환됩니다.')
      : window.confirm('주문을 취소하시겠습니까?\n결제 금액이 실제 환불 처리되며, 사용한 포인트도 즉시 반환됩니다.');

    if (confirmed) {
      onCancelOrder(orderId);
    }
  };

  const handleLoadMoreOrders = () => {
    setVisibleOrdersCount(prev => prev + 5);
  };

  const getTrackingUrl = (company: string, number: string) => {
    if (!number) return null;
    switch (company) {
      case 'CJ대한통운': return `https://www.doortodoor.co.kr/parcel/doortodoor.do?f_invc_no=${number}`;
      case '로젠택배': return `https://www.ilogen.com/web/personal/trace/${number}`;
      case '우체국택배': return `https://service.epost.go.kr/trace.RetrieveDomakskTraceList.comm?sid1=${number}`;
      case '한진택배': return `https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mCode=MN038&wblNum=${number}`;
      default: return null;
    }
  };

  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-black">로그인이 필요한 서비스입니다.</h2>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <h1 className="text-4xl font-black italic tracking-tighter mb-10 uppercase">MY PAGE</h1>
      <div className="flex gap-8 mb-10 border-b border-gray-100">
        <button onClick={() => navigate('?tab=orders')} className={`pb-4 px-2 text-sm font-black transition-all relative ${activeTab === 'orders' ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-900'}`}>
          주문 내역 ({allOrders.length})
          {activeTab === 'orders' && <div className="absolute bottom-0 left-0 w-full h-1 bg-indigo-600 rounded-full animate-in fade-in duration-300"></div>}
        </button>
        <button onClick={() => navigate('?tab=profile')} className={`pb-4 px-2 text-sm font-black transition-all relative ${activeTab === 'profile' ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-900'}`}>
          정보 수정
          {activeTab === 'profile' && <div className="absolute bottom-0 left-0 w-full h-1 bg-indigo-600 rounded-full animate-in fade-in duration-300"></div>}
        </button>
      </div>
      {activeTab === 'orders' && (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
          {allOrders.length === 0 ? (
            <div className="py-24 text-center bg-gray-50/50 rounded-[40px] border border-dashed border-gray-200">
               <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 text-gray-200">
                 <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" strokeWidth={2}/></svg>
               </div>
               <p className="text-gray-400 font-bold italic">주문 내역이 아직 없습니다.</p>
            </div>
          ) : (
            <>
              {displayedOrders.map(order => (
                <div key={order.id} className={`bg-white border border-gray-100 rounded-[32px] p-8 shadow-sm hover:shadow-xl transition-all duration-300 group ${order.status === 'CANCELED' ? 'opacity-60' : ''}`}>
                  <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-50">
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{new Date(order.createdAt).toLocaleDateString()}</p>
                      <p className="font-black text-gray-900 mt-1">{order.orderNumber}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black ${order.status === 'PAYMENT_COMPLETE' ? 'bg-blue-100 text-blue-600' : order.status === 'SHIPPING' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : order.status === 'DELIVERED' ? 'bg-gray-900 text-white' : order.status === 'CANCELED' ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'}`}>{order.status === 'PAYMENT_COMPLETE' ? '결제완료' : order.status === 'PREPARING' ? '상품준비중' : order.status === 'SHIPPING' ? '배송중' : order.status === 'DELIVERED' ? '배송완료' : order.status === 'CANCELED' ? '주문취소' : order.status}</span>
                        {order.status === 'PAYMENT_COMPLETE' && <button onClick={() => handleCancelClick(order.id)} className="text-[10px] font-black text-red-400 hover:text-red-600 underline transition">주문 취소</button>}
                      </div>
                      {(order.status === 'SHIPPING' || order.status === 'DELIVERED') && order.trackingNumber && <a href={getTrackingUrl(order.trackingCompany || 'CJ대한통운', order.trackingNumber) || '#'} target="_blank" rel="noopener noreferrer" className="text-[10px] font-black text-indigo-600 hover:underline flex items-center gap-1 bg-indigo-50 px-2 py-1 rounded-lg transition"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3 h-3"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196 7.5 7.5 0 0010.607 10.607z" /></svg>배송 추적 ({order.trackingCompany})</a>}
                    </div>
                  </div>
                  <div className={`space-y-6 ${order.status === 'CANCELED' ? 'grayscale' : ''}`}>
                    {order.items.map(item => {
                      const optionExtra = Object.values(item.selectedOptions).reduce((sum: number, opt) => sum + ((opt as OptionValue).extraPrice || 0), 0) as number;
                      const addonExtra = (item.selectedAddOns || []).reduce((sum: number, add) => sum + ((add as ProductAddon).price || 0), 0) as number;
                      const productBasePrice = Number(item.price) - optionExtra - addonExtra;
                      const hasDiscount = item.originalPrice && item.originalPrice > productBasePrice;
                      return (
                        <div key={item.cartId} className="flex gap-6 items-start">
                          <img src={item.image} className="w-20 h-20 rounded-[20px] object-cover bg-gray-50 border border-gray-100" />
                          <div className="flex-1">
                            <p className="text-base font-black text-gray-900 leading-tight">{item.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {hasDiscount && <span className="text-[10px] text-gray-300 line-through font-bold">{item.originalPrice?.toLocaleString()}원</span>}
                              <p className="text-xs text-indigo-600 font-black">{productBasePrice.toLocaleString()}원 <span className="text-gray-400 font-bold ml-1">x {item.quantity}개</span></p>
                            </div>
                            {Object.entries(item.selectedOptions).length > 0 && <div className="mt-2 flex flex-wrap gap-2">{Object.entries(item.selectedOptions).map(([k,v]) => (<span key={k} className="text-[9px] bg-indigo-50 px-2 py-1 rounded-md text-indigo-500 font-black tracking-tight border border-indigo-100/30">{k}: {(v as OptionValue).name} {(v as OptionValue).extraPrice > 0 ? `(+${(v as OptionValue).extraPrice.toLocaleString()}원)` : ''}</span>))}</div>}
                            {item.selectedAddOns && item.selectedAddOns.length > 0 && <div className="mt-2 space-y-1">{item.selectedAddOns.map(addon => (<p key={addon.id} className="text-[9px] text-emerald-600 font-black flex items-center gap-1"><span className="text-[8px] bg-emerald-50 px-1.5 py-0.5 rounded text-emerald-500">+ 추가구성</span>{addon.name} ({addon.price.toLocaleString()}원)</p>))}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-8 pt-6 border-t border-gray-50 flex justify-between items-center">
                    <div className="text-xs font-bold text-gray-400 max-w-[60%]"><p className="mb-1">{order.userAddress} {order.userDetailAddress}</p>{order.trackingNumber && <p className="text-indigo-500 font-black">운송장: {order.trackingNumber} ({order.trackingCompany})</p>}</div>
                    <div className="text-right"><div className="flex flex-col items-end gap-1 mb-2"><span className="text-[10px] font-bold text-gray-400">배송비: {order.shippingFee === 0 ? '무료' : `${order.shippingFee.toLocaleString()}원`}</span></div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">TOTAL PAYMENT</p><span className={`text-2xl font-black italic tracking-tighter ${order.status === 'CANCELED' ? 'text-gray-400 line-through' : 'text-indigo-600'}`}>{order.totalAmount.toLocaleString()}원</span></div>
                  </div>
                </div>
              ))}
              {allOrders.length > visibleOrdersCount && (
                <div className="flex flex-col items-center pt-8">
                   <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Viewing {displayedOrders.length} of {allOrders.length} orders</div>
                   <button onClick={handleLoadMoreOrders} className="px-12 py-5 bg-white border-2 border-gray-100 text-gray-900 rounded-[28px] font-black text-xs hover:border-indigo-600 hover:text-indigo-600 transition-all shadow-xl hover:shadow-indigo-50 active:scale-95 flex items-center gap-3">
                     더 많은 주문 내역 보기
                     <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                   </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {activeTab === 'profile' && (
        <div className="space-y-12 animate-in slide-in-from-bottom-4 duration-500">
          <div className="bg-white border border-gray-100 rounded-[48px] p-10 md:p-16 shadow-sm">
            <form onSubmit={handleUpdate} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div><label className="block text-[10px] font-black text-gray-400 mb-3 uppercase tracking-widest">이름</label><input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-gray-900" /></div>
                <div><label className="block text-[10px] font-black text-gray-400 mb-3 uppercase tracking-widest">연락처</label><input type="tel" name="phone" maxLength={13} value={formData.phone} onChange={handleChange} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-gray-900" /></div>
              </div>
              <div className="space-y-3">
                <label className="block text-[10px] font-black text-gray-400 mb-3 uppercase tracking-widest">배송 정보</label>
                <div className="flex gap-2">
                  <input type="text" name="address" readOnly value={formData.address} className="flex-1 px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm cursor-pointer" onClick={handleAddressSearch} placeholder="주소 찾기 클릭" />
                  <button type="button" onClick={handleAddressSearch} className="px-6 py-4 bg-gray-900 text-white font-black text-xs rounded-2xl hover:bg-indigo-600 transition shadow-lg shadow-gray-100">주소 찾기</button>
                </div>
                <input type="text" name="detailAddress" id="editDetailAddress" value={formData.detailAddress} onChange={handleChange} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 outline-none font-black text-sm" placeholder="상세 주소를 입력하세요" />
              </div>
              <div className="pt-8"><button type="submit" className="w-full py-5 bg-indigo-600 text-white font-black rounded-[24px] hover:bg-gray-900 transition shadow-2xl shadow-indigo-100 transform active:scale-95 duration-200">회원 정보 업데이트</button></div>
            </form>
          </div>
          <div className="p-10 border border-red-50 bg-red-50/10 rounded-[40px] flex flex-col items-center gap-4">
            <h3 className="text-gray-900 font-black">더 이상 서비스를 이용하지 않으시나요?</h3>
            <p className="text-xs text-gray-400 font-bold text-center">회원 탈퇴 시 모든 포인트 및 주문 내역이 영구 삭제됩니다.</p>
            <button onClick={handleWithdraw} className="mt-2 text-xs font-black text-red-400 hover:text-red-600 underline transition-colors">회원 탈퇴하기</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPage;
